/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Blueprint, BlueprintData } from '@rta/rta-blueprint-library';
import { useContext, useEffect } from 'react';

import { DESIGN_PAGE_SIZE } from '../../config';
import { getCanonicalDesignId } from '@helpers';
import { hydrateBlueprint } from './hydrate-blueprint';
import { NavType } from '@state';
import { readAutosaveEntries } from './read-autosave-entries';
import { AppContext, DesignActionTypes } from '@context';

import devBlueprintData from '../../../../../local-testing/dev-blueprint-data.json';

export type LoadCustomerDesignsParams = {
	appNav: NavType;
}

// Flag to enable/disable dev mode
const USE_DEV_BLUEPRINT = false;

// eslint-disable-next-line max-lines-per-function
export const useLoadCustomerDesigns = (params: LoadCustomerDesignsParams): void => {
	const { getToken, user } = useKeystoneSession();
	const { appState: { customerDesigns }, appDispatch: dispatch } = useContext(AppContext);
	const { appNav } = params;
	const { email, isLoading } = customerDesigns;

	const navigate = useNavigate();
	const location = useLocation();

	// no email, no designs
	useEffect(() => {
		if (appNav.email === null || appNav.email === '') {
			dispatch({ type: DesignActionTypes.clearCustomerDesigns, payload: null });
			if (appNav.email) navigate(`/${appNav.appMode}`);
		}
	}, [appNav]);

	// load customer designs
	useEffect(() => {
		console.log('use-load-customer-designs:use-effect', appNav);
		const load = async () => {
			// don't run if no nav email or already loading
			if (appNav.email === null || isLoading) {
				return;
			}

			if (appNav.email === email && !appNav.reset) {
				// return if no designs exist
				if (customerDesigns.designs.length === 0) return;
				return;
			}

			// use fristBp to determine which blueprint to navigate to when autoNav is true
			let firstBp: Blueprint | null = null;
			let version: number | null = null;
			let useDraft = (appNav.draft ?? false) || appNav.designVersion === null;

			if (appNav.reset) dispatch({ type: DesignActionTypes.clearCustomerDesigns, payload: null });

			// notify app state that blueprints are being loaded
			dispatch({
				type: DesignActionTypes.loadBlueprintsStart,
				payload: {
					email: appNav.email,
					message: appNav.reset ? 'Refreshing customer designs...' : 'Loading customer designs...'
				}
			});
			// if no email, then clear customer designs and return

			const submissionId = appNav.options?.submissionId ?? appNav.designId;

			// appMode 'v' requires a submissionId, revert to default route if not available
			if (appNav.appMode === 'v' && submissionId === null) {
				navigate('/');
				return;
			}

			// DEV MODE: Use static blueprint data instead of API call
			let blueprints: Array<BlueprintData> = [];
			let error: unknown = null;

			if (USE_DEV_BLUEPRINT) {
				console.log('DEV MODE: Using static blueprint data');
				const data = devBlueprintData as any
				blueprints = [data];
			} else {
				// call API to get customer blueprint(s)
				const { getCustomerBlueprint, getCustomerBlueprints } = await import('@external');

				if (appNav.appMode === 'v') {
					const [bps, err] = await getCustomerBlueprint({ email: appNav.email, submissionId: submissionId ?? '', getToken });
					// the single-design endpoint answers with a one-entry array
					blueprints = Array.isArray(bps) ? (bps as Array<BlueprintData>).filter(Boolean) : bps ? [bps as BlueprintData] : [];
					error = err;
				} else {
					// only the first page loads up front — the rest arrive through Load More
					const [paged, err] = await getCustomerBlueprints({ email: appNav.email, page: 1, limit: DESIGN_PAGE_SIZE, getToken });
					blueprints = (paged?.data ?? []).filter(Boolean);
					error = err;

					if (paged) {
						const { page, pageCount, totalCount, index } = paged;
						dispatch({ type: DesignActionTypes.setDesignPage, payload: { page, pageCount, totalCount, index: index ?? [] }});
					}

					// a deep-linked design can sit past the first page — pull that one in on its own rather than every page up to it
					const entry = appNav.designId && !blueprints.some((bp) => bp?.id === appNav.designId)
						? (paged?.index ?? []).find((e) => e.id === appNav.designId)
						: undefined;

					if (entry) {
						const [requested] = await getCustomerBlueprint({ email: appNav.email, submissionId: getCanonicalDesignId(entry), getToken });
						const requestedData = (Array.isArray(requested) ? requested[0] : requested) as BlueprintData | null;
						if (requestedData) blueprints = [requestedData, ...blueprints];
					}
				}
			}

			// load autosaved entries for customer email
			const autosaveEntries = readAutosaveEntries(appNav.email);

			if (error && blueprints.length === 0) {
				dispatch({ type: DesignActionTypes.loadBlueprintsFailure, payload: null });
				return;
			} else {
				blueprints.forEach((blueprintData, bpIndex) => {
					try {
						const hydrated = hydrateBlueprint({
							blueprintData,
							entryIndex: bpIndex,
							userEmail: user?.email,
							autosaveEntries,
							// appNav.draft or the requested blueprint means the user is here to edit
							forceDraft: appNav.draft === true || appNav.designId === blueprintData.id
						});

						if (!hydrated) return;

						const { blueprint: newBp, isDirty } = hydrated;

						// does the requested version exist
						let bpVersion = null;
						if (appNav.designVersion === null) {
							bpVersion = newBp.kitchens.getActiveVersion();
						} else if (useDraft) {
							bpVersion = newBp.kitchens.findVersion(null);
							if (bpVersion) version = bpVersion?.baseVersion;
						} else {
							bpVersion = newBp.kitchens.findVersion(Number(appNav.designVersion));
						}

						// does the requested version exist
						// let bpVersion = appNav.designVersion === null
						// 	? newBp.kitchens.getActiveVersion()
						// 	: newBp.kitchens.findVersion(Number(appNav.designVersion));

						// override firstBp if there is a Bp that matches the appNav request
						if (appNav.designId && appNav.designId === newBp.id) {
							firstBp = newBp;

							// found it, set the version
							if (bpVersion) {
								// In edit mode, always use a draft for the selected blueprint
								if (appNav.appMode !== 'v') {
									useDraft = true;
									version = bpVersion.version ?? bpVersion.baseVersion ?? null;
								} else if (appNav.draft === true) {
									// View mode with ?draft explicitly requested
									useDraft = true;
									version = bpVersion.version ?? bpVersion.baseVersion ?? null;
								} else {
									// Pure view mode without draft
									useDraft = bpVersion.version === null;
									version = useDraft ? bpVersion.baseVersion : (bpVersion.version ?? null);
								}
							// not found, find latest version and set the version
							} else if (bpVersion === null) {
								bpVersion = newBp.kitchens.getLatestVersion();
								version = bpVersion ? bpVersion.version : null;
								// If appNav.draft is true, we still want useDraft = true
								if (appNav.draft === true) {
									useDraft = true;
								}
							}
						} else if (bpVersion) {
							// For blueprints that don't match the requested designId
							// Respect appNav.draft if it's true
							if (appNav.draft === true) {
								version = bpVersion.version ?? bpVersion.baseVersion ?? null;
							} else {
								version = useDraft ? bpVersion.baseVersion : bpVersion.version;
							}
						}

						// keep track of which Bp was loaded first
						if (firstBp === null) firstBp = newBp;

						// add the Blueprint to application state
						console.log('appNav:addBlueprint', { newBp, version, useDraft, isDirty });
						dispatch({
							type: DesignActionTypes.addBlueprint,
							payload: { blueprint: newBp, version, useDraft, isDirty }
						});
					} catch (e) {
						console.error(
							`[Blueprint Load] Entry ${bpIndex}: failed during Blueprint construction or processing.`,
							{
								error: e instanceof Error ? { message: e.message, stack: e.stack } : e,
								blueprintId: blueprintData?.id ?? '(missing)',
								submissionId: blueprintData?.submissionId ?? '(missing)',
								email: blueprintData?.email ?? '(missing)',
								schemaVersion: blueprintData?.schemaVersion ?? '(missing)',
								hasKitchens: !!blueprintData?.kitchens,
								kitchenCount: Array.isArray(blueprintData?.kitchens) ? blueprintData.kitchens.length : '(not an array)'
							}
						);
						// Continue processing other blueprints even if one fails
					}
				});
			}

			// load any remaining autosave entries
			// Object.values(autosaveEntries).forEach((value) => {
			// 	if (value.data) {
			// 		value.data.version = null;
			// 		const newBp = new Blueprint({ userEmail: user?.email, dataIn: { id: value.blueprintId, email: appNav.email ?? 'SYSTEM', kitchens: [value.data] }});
			// 		newBp.kitchens[0].isLocal = true;

			// 		if (appNav.designId && appNav.designId === newBp.id) {
			// 			firstBp = newBp;
			// 			version = newBp.kitchens[0].version ?? 1;
			// 			useDraft = true;
			// 		} else if (firstBp === null) firstBp = newBp;

			// 		console.log('appNav:addBlueprint', { newBp, version: newBp.kitchens[0].version, useDraft: true, isDirty: true });
			// 		dispatch({
			// 			type: DesignActionTypes.addBlueprint,
			// 			payload: { blueprint: newBp, version: newBp.kitchens[0].version, useDraft: true, isDirty: true }
			// 		});
			// 	}
			// });


			if (firstBp !== null) {
				const bpNavId = appNav.designId ?? (firstBp as Blueprint).id;
				console.log('----------------------------');
				console.log('appNav:', { firstBp, reqVersion: appNav.designVersion, version, reqDraft: appNav.draft, useDraft });
				console.log('----------------------------');
				const basePath = `/${appNav.appMode}/${appNav.email}/${bpNavId}`;
				const pathWithVersion = (version !== null && version !== undefined) ? `${basePath}/${version}` : basePath;
				const target = `${pathWithVersion}${useDraft ? '?draft' : ''}`;
				if (`${location.pathname}${location.search}` !== target) {
					navigate(target);
				}
			}

			dispatch({ type: DesignActionTypes.loadBlueprintsSuccess, payload: null });
		};

		load();
	}, [appNav, email]);
};
